import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon, Text } from '@shopify/polaris';
import {
  ChartLineIcon,
  ClipboardCheckIcon,
  ConnectIcon,
  CreditCardIcon,
  HomeIcon,
  ListBulletedIcon,
  MegaphoneIcon,
  MenuIcon,
  PlusCircleIcon,
  SettingsIcon,
  XIcon,
} from '@shopify/polaris-icons';

import '../../styles/agent-console-tokens.css';
import { ChangelogBadge } from '../changelog/ChangelogBadge';
import { WhatChangedButton } from '../whatChanged/WhatChangedButton';
import { PendingApprovalsBadge } from '../approvals/PendingApprovalsBadge';
import { NotificationBell } from '../NotificationBell';
import styles from './AppShell.module.css';
import '../../styles/smb.css';

type IconSource = typeof HomeIcon;

interface NavItem {
  label: string;
  path: string;
  icon?: IconSource;
  /** Exact-path match only. Defaults to true; false lets sub-routes light
   * this item up too (only Marketing team needs that today). */
  end?: boolean;
}

/** Six primary destinations — the whole point of an SMB owner being able
 * to scan the sidebar in one glance. Anything else lives in "More". */
const PRIMARY_NAV: NavItem[] = [
  { label: 'Overview', path: '/dashboards', icon: HomeIcon },
  { label: 'Marketing team', path: '/marketing-agents', icon: MegaphoneIcon, end: false },
  { label: 'Reports', path: '/analytics', icon: ChartLineIcon },
  { label: 'Recommendations', path: '/insights', icon: ListBulletedIcon },
  { label: 'Approvals', path: '/approvals', icon: ClipboardCheckIcon },
  { label: 'Connections', path: '/connectors', icon: ConnectIcon },
];

/** Every other route that used to live in the old flat header, kept
 * reachable behind one disclosure with plain-language labels. */
const MORE_NAV: NavItem[] = [
  { label: 'Ask a question', path: '/ask' },
  { label: 'Alerts', path: '/alerts' },
  { label: 'Action log', path: '/actions' },
  { label: 'Team settings', path: '/agents' },
  { label: 'Team enablement', path: '/agents/enablement' },
  { label: 'Data confidence', path: '/beliefs' },
  { label: 'AI settings', path: '/settings/llm' },
  { label: 'Upload data', path: '/data-uploads' },
  { label: 'Sync health', path: '/sync-health' },
  { label: "What's new", path: '/whats-new' },
];

// Deliberately absent: /admin/plans. It stays a live, URL-reachable route
// for whoever administers plans, but it is internal tooling rather than a
// business owner's destination, so it never sits in the nav they scan.
// /analytics appears once, as "Reports" above — a second entry here would
// light two rows at the same time.

function NavRow({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.path}
      end={item.end ?? true}
      className={({ isActive }) => (isActive ? styles.navItemActive : styles.navItem)}
    >
      {item.icon && <Icon source={item.icon} />}
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement | null>(null);

  // Opening the drawer moves focus into it, so a keyboard or screen-reader
  // user lands on the navigation they just asked for instead of continuing
  // through the top bar behind it.
  useEffect(() => {
    if (!navOpen) return;
    const frame = requestAnimationFrame(() => sidebarRef.current?.querySelector<HTMLElement>('a, button')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [navOpen]);

  // A route change is the one unambiguous "the merchant is done with the
  // mobile drawer" signal — closing it on any other interaction (a
  // scroll, a badge poll) would fight the merchant's own taps.
  useEffect(() => {
    setNavOpen(false);
    if (sidebarRef.current?.contains(document.activeElement)) document.getElementById('main-content')?.focus();
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        const nodes = Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>('a, button, summary') ?? []).filter(el => {
          const closed = el.closest('details:not([open])');
          return el.getClientRects().length > 0 && (!closed || el === closed.querySelector('summary'));
        });
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (!sidebarRef.current?.contains(document.activeElement)) { event.preventDefault(); first?.focus(); }
        else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
      if (event.key === 'Escape') {
        setNavOpen(false);
        document.getElementById('nav-toggle')?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  useEffect(() => {
    if (!navOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const desktop = window.matchMedia('(min-width: 900px)');
    const closeOnDesktop = () => { if (desktop.matches) setNavOpen(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => { document.body.style.overflow = previous; desktop.removeEventListener('change', closeOnDesktop); };
  }, [navOpen]);

  const isOnWhatsNewPage = location.pathname === '/whats-new';
  const title = location.pathname === '/welcome' ? 'Business setup' : [...PRIMARY_NAV, ...MORE_NAV, {path: '/billing', label: 'Billing'}].find(item => location.pathname === item.path || (item.path === '/marketing-agents' && location.pathname.startsWith(item.path + '/')))?.label ?? 'Your workspace';

  return (
    <div className={styles.shell}>
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>

      <div
        className={`${styles.backdrop} ${navOpen ? styles.backdropOpen : ''}`}
        onClick={() => { setNavOpen(false); document.getElementById('nav-toggle')?.focus(); }}
        aria-hidden="true"
      />

      <nav
        id="app-sidebar"
        ref={sidebarRef}
        aria-label="Primary"
        className={`${styles.sidebar} ${navOpen ? styles.sidebarOpen : ''}`}
      >
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            M
          </span>
          <Text as="span" variant="headingSm" fontWeight="bold">
            Markinsight
          </Text>
          <span className={styles.drawerClose}><Button icon={XIcon} accessibilityLabel="Close menu" variant="tertiary" onClick={() => {setNavOpen(false); document.getElementById('nav-toggle')?.focus();}} /></span>
        </div>

        <Button
          variant="primary"
          fullWidth
          icon={PlusCircleIcon}
          onClick={() => navigate('/welcome')}
        >
          Set up your business
        </Button>

        <ul className={styles.navList}>
          {PRIMARY_NAV.map((item) => (
            <li key={item.path}>
              <NavRow item={item} />
            </li>
          ))}
        </ul>

        <div className={styles.navSection}>
          <NavRow item={{ label: 'Billing', path: '/billing', icon: CreditCardIcon }} />

          <details className={styles.more} key={location.pathname} open={MORE_NAV.some(item => item.path === location.pathname)}>
            <summary>
              <Icon source={SettingsIcon} />
              <span>More</span>
            </summary>
            <ul className={styles.navList}>
              {MORE_NAV.map((item) => (
                <li key={item.path}>
                  <NavRow item={item} />
                </li>
              ))}
            </ul>
            <div className={styles.debugTool}><WhatChangedButton variant="inline" showBadge refreshInterval={60000} /></div>
          </details>
        </div>
      </nav>

      <div className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.mobileToggle}>
            <Button
              id="nav-toggle"
              icon={navOpen ? XIcon : MenuIcon}
              accessibilityLabel={navOpen ? 'Close navigation' : 'Open navigation'}
              ariaExpanded={navOpen}
              ariaControls="app-sidebar"
              variant="tertiary"
              onClick={() => setNavOpen((open) => !open)}
            />
          </div>
          <span className={styles.pageLabel}>{title}</span>
          <span className={styles.mobileBrand}>Markinsight</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
            <PendingApprovalsBadge onClick={() => navigate('/approvals')} refreshInterval={60000} />
            <NotificationBell refreshInterval={60000} />
            {!isOnWhatsNewPage && (
              <span className={styles.desktopUtility}><ChangelogBadge onClick={() => navigate('/whats-new')} showLabel label="What's New" refreshInterval={60000} /></span>
            )}
          </div>
        </header>

        <main id="main-content" className={styles.content} tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default AppShell;
